/**
 * Unified WebSocket Hook
 * 
 * React hook that provides WebSocket functionality using the unified WebSocket service.
 * Handles authentication, channel subscriptions, and type-safe message handling.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import {
  getWebSocketService,
  UnifiedWebSocketService
} from '../services/websocketService';
import * as sessionStore from '../services/sessionStore';
import {
  WebSocketChannel,
  WebSocketConnectionState,
  WebSocketEventHandlers,
  WebSocketConfig,
  WebSocketConnectionInfo,
  WebSocketMetrics,
  WebSocketMessage,
  WebSocketMessageType
} from '../types/websocket';

interface UseUnifiedWebSocketProps {
  config?: WebSocketConfig;
  channels?: WebSocketChannel[];
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (error: Error) => void;
  onMessage?: (message: WebSocketMessage) => void;
  eventHandlers?: Partial<WebSocketEventHandlers>;
  autoConnect?: boolean;
}

interface UseUnifiedWebSocketReturn {
  // Connection state
  connectionState: WebSocketConnectionState;
  connectionInfo: WebSocketConnectionInfo | null;
  isConnected: boolean;
  isAuthenticated: boolean;
  
  // Methods
  connect: () => Promise<void>;
  disconnect: () => void;
  sendMessage: <T = any>(message: any, requireAck?: boolean) => Promise<T | void>;
  subscribeToChannel: (channel: WebSocketChannel, filters?: Record<string, any>) => Promise<void>;
  unsubscribeFromChannel: (channel: WebSocketChannel) => Promise<void>;
  updateToken: (token: string) => void;
  
  // Utility
  subscribedChannels: Set<WebSocketChannel>;
  metrics: WebSocketMetrics | null;
  lastError: Error | null;
}

export const useUnifiedWebSocket = ({
  config = {},
  channels = [],
  onConnect,
  onDisconnect,
  onError,
  onMessage,
  eventHandlers = {},
  autoConnect = true
}: UseUnifiedWebSocketProps = {}): UseUnifiedWebSocketReturn => {
  
  const serviceRef = useRef<UnifiedWebSocketService | null>(null);
  const [connectionState, setConnectionState] = useState<WebSocketConnectionState>(
    WebSocketConnectionState.DISCONNECTED
  );
  const [connectionInfo, setConnectionInfo] = useState<WebSocketConnectionInfo | null>(null);
  const [subscribedChannels, setSubscribedChannels] = useState<Set<WebSocketChannel>>(new Set());
  const [metrics, setMetrics] = useState<WebSocketMetrics | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);

  // Initialize service
  useEffect(() => {
    if (!serviceRef.current) {
      // Get JWT token from the active account's session
      const token = sessionStore.getActiveAccessToken() || '';
      
      const serviceConfig: WebSocketConfig = {
        autoConnect: false, // We'll handle connection manually
        token,
        ...config
      };

      serviceRef.current = getWebSocketService(serviceConfig);

      // Set up event handlers
      const service = serviceRef.current;

      service.on('onConnect', () => {
        setConnectionState(WebSocketConnectionState.CONNECTED);
        setConnectionInfo(service.getConnectionInfo());
        onConnect?.();
      });

      service.on('onDisconnect', (reason: string) => {
        setConnectionState(WebSocketConnectionState.DISCONNECTED);
        setConnectionInfo(null);
        setSubscribedChannels(new Set());
        onDisconnect?.(reason);
      });

      service.on('onError', (error: Error) => {
        setLastError(error);
        setConnectionState(WebSocketConnectionState.ERROR);
        onError?.(error);
      });

      service.on('onAuthSuccess', (userId: string, channels: string[]) => {
        setConnectionState(WebSocketConnectionState.AUTHENTICATED);
        setConnectionInfo(service.getConnectionInfo());
        console.log(`[WebSocket Hook] Authenticated as ${userId} with channels:`, channels);
      });

      service.on('onAuthFailure', (error: string) => {
        setLastError(new Error(error));
        setConnectionState(WebSocketConnectionState.ERROR);
        console.error('[WebSocket Hook] Authentication failed:', error);
      });

      service.on('onChannelSubscribed', (channel: WebSocketChannel) => {
        setSubscribedChannels(prev => new Set([...Array.from(prev), channel]));
        setConnectionInfo(service.getConnectionInfo());
      });

      service.on('onChannelUnsubscribed', (channel: WebSocketChannel) => {
        setSubscribedChannels(prev => {
          const next = new Set(prev);
          next.delete(channel);
          return next;
        });
        setConnectionInfo(service.getConnectionInfo());
      });

      service.on('onMessage', (message: WebSocketMessage) => {
        setMetrics(service.getMetrics());
        onMessage?.(message);
      });

      // Set up type-specific event handlers
      // Use explicit type handling to avoid complex union types
      const handlers = eventHandlers as any;
      Object.keys(handlers).forEach((eventType) => {
        const handler = handlers[eventType];
        if (handler) {
          service.on(eventType as any, handler);
        }
      });
    }

    return () => {
      // Cleanup will happen in the final useEffect cleanup
    };
  }, []); // Only run once

  // Handle auto-connect
  useEffect(() => {
    if (autoConnect && serviceRef.current) {
      // Swallow connect rejection here; the error is already surfaced via
      // setLastError/onError. Leaving it unhandled triggers the CRA "Uncaught
      // runtime errors" overlay on every failed/aborted WS connect.
      connect().catch(() => {});
    }
  }, [autoConnect]);

  // Auto-subscribe to channels
  useEffect(() => {
    if (connectionState === WebSocketConnectionState.AUTHENTICATED && channels.length > 0) {
      const subscribeToChannels = async () => {
        for (const channel of channels) {
          if (!subscribedChannels.has(channel)) {
            try {
              await subscribeToChannel(channel);
            } catch (error) {
              console.error(`Failed to subscribe to channel ${channel}:`, error);
            }
          }
        }
      };

      subscribeToChannels();
    }
  }, [connectionState, channels]);

  // Update connection info periodically
  useEffect(() => {
    if (connectionState === WebSocketConnectionState.CONNECTED || 
        connectionState === WebSocketConnectionState.AUTHENTICATED) {
      
      const interval = setInterval(() => {
        if (serviceRef.current) {
          setConnectionInfo(serviceRef.current.getConnectionInfo());
          setMetrics(serviceRef.current.getMetrics());
        }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [connectionState]);

  // Methods
  const connect = useCallback(async (): Promise<void> => {
    if (!serviceRef.current) return;
    
    try {
      setLastError(null);
      await serviceRef.current.connect();
    } catch (error) {
      setLastError(error as Error);
      throw error;
    }
  }, []);

  const disconnect = useCallback((): void => {
    if (!serviceRef.current) return;
    
    serviceRef.current.disconnect();
    setConnectionState(WebSocketConnectionState.DISCONNECTED);
    setConnectionInfo(null);
    setSubscribedChannels(new Set());
  }, []);

  const sendMessage = useCallback(async <T = any>(
    message: any,
    requireAck = false
  ): Promise<T | void> => {
    if (!serviceRef.current) {
      throw new Error('WebSocket service not initialized');
    }
    
    return serviceRef.current.sendMessage<T>(message, requireAck);
  }, []);

  const subscribeToChannel = useCallback(async (
    channel: WebSocketChannel,
    filters?: Record<string, any>
  ): Promise<void> => {
    if (!serviceRef.current) {
      throw new Error('WebSocket service not initialized');
    }
    
    await serviceRef.current.subscribeToChannel(channel, filters);
  }, []);

  const unsubscribeFromChannel = useCallback(async (
    channel: WebSocketChannel
  ): Promise<void> => {
    if (!serviceRef.current) {
      throw new Error('WebSocket service not initialized');
    }
    
    await serviceRef.current.unsubscribeFromChannel(channel);
  }, []);

  const updateToken = useCallback((token: string): void => {
    if (!serviceRef.current) return;
    
    // Store the token in the active account's session
    const activeId = sessionStore.getActiveMemberId();
    if (activeId !== null) sessionStore.updateTokens(activeId, token);
    serviceRef.current.updateToken(token);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (serviceRef.current) {
        serviceRef.current.disconnect();
        serviceRef.current = null;
      }
    };
  }, []);

  // Computed values
  const isConnected = connectionState === WebSocketConnectionState.CONNECTED ||
                     connectionState === WebSocketConnectionState.AUTHENTICATED;
                     
  const isAuthenticated = connectionState === WebSocketConnectionState.AUTHENTICATED;

  return {
    // Connection state
    connectionState,
    connectionInfo,
    isConnected,
    isAuthenticated,
    
    // Methods
    connect,
    disconnect,
    sendMessage,
    subscribeToChannel,
    unsubscribeFromChannel,
    updateToken,
    
    // Utility
    subscribedChannels,
    metrics,
    lastError
  };
};

// Convenience hooks for specific use cases

/**
 * Hook for conversation-related WebSocket functionality
 */
export const useConversationWebSocket = (conversationId?: number) => {
  const [newMessages, setNewMessages] = useState<any[]>([]);
  const [typingUsers, setTypingUsers] = useState<Set<number>>(new Set());

  const websocket = useUnifiedWebSocket({
    channels: [WebSocketChannel.CONVERSATIONS, WebSocketChannel.MESSAGES],
    eventHandlers: {
      [WebSocketMessageType.NEW_MESSAGE]: (payload) => {
        if (!payload) return;
        if (!conversationId || payload.conversation_id === conversationId) {
          setNewMessages(prev => [...prev, payload]);
        }
      },
      [WebSocketMessageType.TYPING_START]: (payload) => {
        if (!payload) return;
        if (!conversationId || payload.conversation_id === conversationId) {
          setTypingUsers(prev => new Set([...Array.from(prev), payload.user_id]));
        }
      },
      [WebSocketMessageType.TYPING_STOP]: (payload) => {
        if (!payload) return;
        if (!conversationId || payload.conversation_id === conversationId) {
          setTypingUsers(prev => {
            const next = new Set(prev);
            next.delete(payload.user_id);
            return next;
          });
        }
      }
    }
  });

  const sendTypingIndicator = useCallback(async (isTyping: boolean) => {
    if (!conversationId) return;
    
    await websocket.sendMessage({
      type: isTyping ? WebSocketMessageType.TYPING_START : WebSocketMessageType.TYPING_STOP,
      payload: {
        conversation_id: conversationId,
        is_typing: isTyping,
        user_id: 0 // Will be filled by backend with actual user ID
      }
    });
  }, [conversationId, websocket.sendMessage]);

  return {
    ...websocket,
    newMessages,
    typingUsers,
    sendTypingIndicator,
    clearNewMessages: () => setNewMessages([])
  };
};

/**
 * Hook for order-related WebSocket functionality
 */
export const useOrderWebSocket = () => {
  const [newOrders, setNewOrders] = useState<any[]>([]);
  const [orderUpdates, setOrderUpdates] = useState<any[]>([]);
  const [verificationAlerts, setVerificationAlerts] = useState<any[]>([]);

  const websocket = useUnifiedWebSocket({
    channels: [WebSocketChannel.ORDERS, WebSocketChannel.ORDER_VERIFICATION],
    eventHandlers: {
      [WebSocketMessageType.NEW_ORDER]: (payload) => {
        setNewOrders(prev => [...prev, payload]);
      },
      [WebSocketMessageType.ORDER_STATUS_CHANGED]: (payload) => {
        setOrderUpdates(prev => [...prev, payload]);
      },
      [WebSocketMessageType.ORDER_VERIFICATION_NEEDED]: (payload) => {
        setVerificationAlerts(prev => [...prev, payload]);
      }
    }
  });

  return {
    ...websocket,
    newOrders,
    orderUpdates,
    verificationAlerts,
    clearNewOrders: () => setNewOrders([]),
    clearOrderUpdates: () => setOrderUpdates([]),
    clearVerificationAlerts: () => setVerificationAlerts([])
  };
};

export default useUnifiedWebSocket;